import React, { useEffect, useRef, useState, useCallback } from "react";
import client from '../../contentfulClientToken';
import '../ImageScroller.scss';
import { getImageUrl } from '../../utils/imageUtils';
 

/**
 * Generic year/tag scroller that fetches images by Contentful asset tag
 * and renders the infinite scroll + fixed viewer UI.
 */
export default function YearTag({ tagId, handwritingYear, yearsIsVisible, monthsIsVisible, keywordsIsVisible }) {
    const [images, setImages] = useState([]);
    const [activeImage, setActiveImage] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [duplicatedImages, setDuplicatedImages] = useState([]);

    const imageRefs = useRef([]);
    const lastScrollY = useRef(0);
    const pendingScrollYRef = useRef(0);
    const tickingRef = useRef(false);
    const containerRef = useRef(null);
    const topSentinelRef = useRef(null);
    const bottomSentinelRef = useRef(null);
    const imagesRef = useRef([]);
    const activeImageRef = useRef('');
    const duplicatedImagesRef = useRef([]);
    const handleImageLoad = useCallback(() => {
        // no-op image onLoad handler
    }, []);

    // Keep refs in sync with state
    useEffect(() => {
        imagesRef.current = images;
        activeImageRef.current = activeImage;
        duplicatedImagesRef.current = duplicatedImages;
    }, [images, activeImage, duplicatedImages]);

    // Initialize viewer when images first load
    useEffect(() => {
        if (images.length > 0) {
            setDuplicatedImages(images);
            const firstImageUrl = images[0] || '';
            setActiveImage(firstImageUrl ? getImageUrl(firstImageUrl) : '');
            setActiveIndex(0);
        }
    }, [images]);

    const getOriginalIndex = useCallback((duplicatedIndex) => {
        const originalLength = imagesRef.current.length || 0;
        return duplicatedIndex % originalLength;
    }, []);

    const updateActiveImageByTop = useCallback(() => {
        let topIndex = -1;
        let topDistance = Infinity;

        imageRefs.current.forEach((img, index) => {
            if (!img) return;
            const rect = img.getBoundingClientRect();
            if (rect.top >= 0 && rect.top < topDistance) {
                topDistance = rect.top;
                topIndex = index;
            }
        });

        if (topIndex !== -1 && duplicatedImagesRef.current[topIndex]) {
            const newUrl = getImageUrl(duplicatedImagesRef.current[topIndex]);
            if (newUrl !== activeImageRef.current) {
                setActiveImage(newUrl);
            }
            setActiveIndex(getOriginalIndex(topIndex));
        }
    }, [getOriginalIndex]);

    const handleScroll = useCallback(() => {
        // Capture latest scroll position and throttle work to next animation frame
        pendingScrollYRef.current = window.scrollY;

        if (tickingRef.current) return;
        tickingRef.current = true;

        requestAnimationFrame(() => {
            const currentScrollY = pendingScrollYRef.current;
            const direction = currentScrollY > lastScrollY.current ? 'down' : 'up';

            const nearBottom = window.innerHeight + currentScrollY >= document.documentElement.scrollHeight - 250;
            const nearTop = currentScrollY <= 250;

            if (nearBottom && direction === 'down') {
                setDuplicatedImages(prev => [...prev, ...imagesRef.current]);
            } else if (nearTop && direction === 'up') {
                setDuplicatedImages(prev => [...imagesRef.current, ...prev]);
            }

            // Update the active image based on the top-most visible thumbnail
            updateActiveImageByTop();

            lastScrollY.current = currentScrollY;
            tickingRef.current = false;
        });
    }, [updateActiveImageByTop]);

    // Remove heavy scroll work; rely on observers instead
    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // IntersectionObserver to trigger recalculation of the active image
    useEffect(() => {
        if (!imageRefs.current || imageRefs.current.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((e) => e.isIntersecting)) return;
                // Recompute based on actual top-most visible element
                updateActiveImageByTop();
            },
            {
                root: null,
                rootMargin: '0px 0px -90% 0px',
                threshold: 0,
            }
        );

        imageRefs.current.forEach((imgEl) => {
            if (imgEl) observer.observe(imgEl);
        });

        return () => observer.disconnect();
    }, [duplicatedImages, updateActiveImageByTop]);

    // IntersectionObservers for infinite duplication using sentinels
    useEffect(() => {
        const topEl = topSentinelRef.current;
        const bottomEl = bottomSentinelRef.current;
        if (!topEl && !bottomEl) return;

        let lastAppendAt = 0;
        let lastPrependAt = 0;
        const minPixelGap = 800; // avoid duplicating too often

        const onIntersect = (entries) => {
            const nowY = window.scrollY;
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                if (entry.target === bottomEl) {
                    if (Math.abs(nowY - lastAppendAt) < minPixelGap) return;
                    lastAppendAt = nowY;
                    setDuplicatedImages(prev => [...prev, ...imagesRef.current]);
                } else if (entry.target === topEl) {
                    if (Math.abs(nowY - lastPrependAt) < minPixelGap) return;
                    lastPrependAt = nowY;
                    setDuplicatedImages(prev => [...imagesRef.current, ...prev]);
                }
            });
        };

        const sentinelObserver = new IntersectionObserver(onIntersect, {
            root: null,
            rootMargin: '500px',
            threshold: 0,
        });

        if (bottomEl) sentinelObserver.observe(bottomEl);
        if (topEl) sentinelObserver.observe(topEl);

        return () => sentinelObserver.disconnect();
    }, [images]);

    // Avoid browser restoring previous scroll, and ensure start at top
    useEffect(() => {
        const { history } = window;
        const prev = history && history.scrollRestoration;
        if (history && 'scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
        });
        return () => {
            if (history && 'scrollRestoration' in history && prev) {
                history.scrollRestoration = prev;
            }
        };
    }, []);

    // Ensure initial position and active image are correct without requiring a resize
    useEffect(() => {
        // Snap to top and compute the initial active image once DOM is ready
        window.scrollTo(0, 0);
        requestAnimationFrame(() => {
            updateActiveImageByTop();
        });
    }, [updateActiveImageByTop]);

    // Fetch images with given Contentful asset tag
    useEffect(() => {
        const query = {
            content_type: 'month',
            limit: 500,
            order: '-sys.createdAt',
        };
        client.getEntries(query)
            .then(response => {
                if (response.items.length > 0 && response.includes && response.includes.Asset) {
                    const filteredAssets = response.includes.Asset.filter(asset =>
                        Array.isArray(asset?.metadata?.tags) && asset.metadata.tags.some(tag => tag.sys.id === tagId)
                    );

                    const sortedAssets = filteredAssets.sort((a, b) =>
                        new Date(b.sys.createdAt) - new Date(a.sys.createdAt)
                    );

                    const imageUrls = sortedAssets.map(asset => asset.fields.file.url);
                    setImages(imageUrls);
                    // Reset scroll to top when changing tag/images
                    requestAnimationFrame(() => {
                        window.scrollTo(0, 0);
                    });
                }
            })
            .catch(error => {
                console.error(error);
            });
    }, [tagId]);

    const handwritingSrc = `../../content/handwriting/years/${handwritingYear}.png`;

    return (
        <div className="container">
            <div className="picture-number">
                <p>{Math.max(1, activeIndex + 1)}</p>
            </div>

            <div className="fixed-image-viewer">
                {activeImage ? (
                    <img src={activeImage} alt="Currently visible" />
                ) : (
                    <div className="fixed-image-loading">loading</div>
                )}
            </div>

            <div className="scroll-container" ref={containerRef}>
                <div ref={topSentinelRef} style={{ height: 1 }} />
                {duplicatedImages.map((imageUrl, index) => (
                    <img
                        key={`${imageUrl}-${index}`}
                        src={getImageUrl(imageUrl)}
                        loading="lazy"
                        onLoad={handleImageLoad}
                        data-index={index}
                        ref={(el) => (imageRefs.current[index] = el)}
                        alt="Timothée Lambrecq photography"
                        className="scroll-image"
                    />
                ))}
                <div ref={bottomSentinelRef} style={{ height: 1 }} />
            </div>

            {!(yearsIsVisible || monthsIsVisible || keywordsIsVisible) && (
                <div className="handwriting-title">
                    <img src={handwritingSrc} alt={String(handwritingYear)} />
                </div>
            )}
        </div>
    );
}

